import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalculator from './BookingCalculator';

const Hero = () => {
  // Adjust these opacity values (0-100) to control background visibility
  const MOBILE_OPACITY = 65;  // Change this for mobile (0 = fully transparent, 100 = fully opaque)
  const DESKTOP_OPACITY = 77; // Change this for desktop (0 = fully transparent, 100 = fully opaque)

  return (
    <div className="relative min-h-screen pt-20">
      {/* Australian Flag Banner */}
      <div className="absolute top-20 left-0 right-0 bg-blue-700/95 backdrop-blur-sm py-3 z-10 shadow-md overflow-hidden">
        {/* Shine Animation Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          style={{
            animation: 'shine 3s infinite',
            transform: 'translateX(-100%)',
          }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-3 relative z-10">
          <img
            src="/flagausi.jpg"
            alt="Australian Flag"
            className="w-10 h-7 object-cover rounded shadow-sm"
          />
          <span className="text-white font-medium text-sm sm:text-base">
            WA owned business - Perth, Australia
          </span>
        </div>

        {/* Add keyframes style */}
        <style jsx>{`
          @keyframes shine {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}</style>
      </div>

      {/* Background Image with Overlay - Different images for mobile/desktop */}
      <div className="absolute inset-0 -z-10">
        {/* Mobile Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
          style={{
            backgroundImage: 'url("/vanimg.jpeg")',
          }}
        ></div>

        {/* Desktop Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden lg:block"
          style={{
            backgroundImage: 'url("/vanimg.jpeg")',
          }}
        ></div>

        {/* Gradient Overlay - Opacity controlled by variables above */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 lg:hidden"
          style={{ opacity: MOBILE_OPACITY / 100 }}
        ></div>
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 hidden lg:block"
          style={{ opacity: DESKTOP_OPACITY / 100 }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 mt-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-sm font-medium text-slate-700">5/5</span>
              <span className="text-sm text-slate-500">· Trusted by</span>
              <Link to="/reviews" className="text-sm font-medium text-blue-600 hover:underline">
                100+ Happy Customers
              </Link>
            </div>
            
            {/* Main Heading */}
            <h1
              style={{
                fontSize: "clamp(2.25rem, 4vw, 3rem)",
                fontWeight: 800,
                lineHeight: 1.2,
                color: "#0f172a",
              }}
            >
              <span style={{ display: "block" }}>Premium</span>

              {/* Continuous Shine Text */}
              <span
                style={{
                  display: "inline-block",
                  position: "relative",
                  fontWeight: 800,
                  background:
                    "linear-gradient(120deg, #2563eb 20%, #ffffff 40%, #2563eb 60%)",
                  backgroundSize: "300% 100%",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  animation: "textShine 5.8s linear infinite",
                  paddingRight: "1.5rem",
                }}
              >
                Home Cleaning

                {/* Blinking Star */}
                <span
                  style={{
                    position: "absolute",
                    top: "-0.9rem",
                    right: "0",
                    fontSize: "2rem",
                    color: "#facc15",
                    animation: "blink 2.6s ease-in-out infinite",
                    pointerEvents: "none",
                  }}
                >
                  ✦
                </span>
              </span>

              {/* <br /> */}

              {/* <span style={{ display: "block" }}>Cleaning</span> */}

              {/* Keyframes */}
              <style>
                {`
      @keyframes textShine {
        0% {
          background-position: 0% 0;
        }
        100% {
          background-position: 300% 0;
        }
      }

      @keyframes blink {
        0%, 100% {
          opacity: 0;
          transform: scale(0.8);
        }
        50% {
          opacity: 1;
          transform: scale(1.2);
        }
      }
    `}
              </style>
            </h1>

            <p
              style={{
                marginTop: "0.75rem",
                color: "#64748b",
                fontWeight: 500,
              }}
            >
              Clean spaces. Zero stress.
            </p>






            {/* Description */}
            <p className="text-lg text-slate-600 leading-relaxed text-justify">
              We hold the strongest reputation for quality cleaning and customer satisfaction in Perth. Our expert team delivers top-rated cleaning services leaving your home spotless and inviting.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Customizable', icon: '⚙️' },
                { label: 'Price Upfront', icon: '💰' },
                { label: 'No Sign-ups', icon: '✓' },
                { label: 'Fast Checkout', icon: '⚡' }
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-center space-x-3 bg-white/40 backdrop-blur-md p-4 shadow-lg border border-white/60 hover:bg-white/60 hover:shadow-xl transition-all duration-300"
                  style={{ borderRadius: '24px' }}
                >
                  <span className="text-2xl">{feature.icon}</span>
                  <span className="font-medium text-slate-700">{feature.label}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8">
                <Link to="/book">
                  Book Your Clean Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/services">View Our Services</Link>
              </Button>
            </div>
          </div>

          {/* Right Content - Booking Calculator */}
          <div className="lg:pl-8">
            <BookingCalculator showbtn = {true}/>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;